import Link from "next/link";
import { useEffect, useId, useState } from "react";
import { ChevronRight, ExternalLink, LucideIcon } from "lucide-react";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuAction,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  useSidebar,
} from "./ui/sidebar";
import { usePathname, useRouter } from "next/navigation";
import { startNavigationFeedback } from "@/lib/navigation-feedback";

export type NavBranch = { title: string; url: string; children?: NavBranch[] };

type NavSecondaryProps = {
  asLinks?: boolean;
  items: {
    title: string;
    url: string;
    className?: string;
    pages?: {
      title: string;
      label?: React.ReactNode;
      url: string;
      exactMatch?: boolean;
      icon: LucideIcon;
      iconClassName?: string;
      children?: NavBranch[];
      badge?: number;
      statusBadge?: { label: string; className: string };
    }[];
  }[];
} & React.ComponentProps<typeof SidebarGroup>;

export function NavSecondary({ items, asLinks = false }: NavSecondaryProps) {
  const router = useRouter();
  const pathname = usePathname();
  const { setOpenMobile, isMobile } = useSidebar();

  return (
    <>
      {items.map((item) => (
        <SidebarGroupContent
          key={item.title}
          className={`px-2 pb-4 pt-2 ${item.className}`}
        >
          <SidebarGroupLabel className="font-medium text-muted">
            {item.title}
          </SidebarGroupLabel>
          <SidebarMenu>
            {item.pages?.map((page) => {
              const external = /^https?:\/\//.test(page.url);
              const content = (
                <>
                  <page.icon
                    className={`text-muted ${["Discord", "PowerOn Pro", "PowerOnPro"].includes(page.icon.displayName || "") && "fill-muted"} ${page.iconClassName ?? ""}`}
                  />
                  <span className="min-w-0 flex-1 truncate">
                    {page.label ?? page.title}
                  </span>
                  {asLinks && external && (
                    <ExternalLink
                      className="!size-3.5 shrink-0 text-muted"
                      aria-hidden
                    />
                  )}
                </>
              );
              return (
                <SidebarMenuItem
                  key={page.title}
                  className={page.children ? "min-w-0 flex-col" : undefined}
                >
                  <SidebarMenuButton
                    variant="custom"
                    asChild={asLinks}
                    onClick={(event) => {
                      if (asLinks) {
                        if (
                          event.metaKey ||
                          event.ctrlKey ||
                          event.shiftKey ||
                          event.altKey
                        )
                          return;
                        if (isMobile) setOpenMobile(false);
                        if (!external) startNavigationFeedback(page.url);
                        return;
                      }
                      if (isMobile) setOpenMobile(false);
                      if (page.url.includes("http")) {
                        window.open(page.url, "_blank");
                      } else {
                        startNavigationFeedback(page.url);
                        router.push(page.url);
                      }
                    }}
                    isActive={
                      page.exactMatch
                        ? pathname === page.url
                        : pathname.includes(page.url)
                    }
                    className={`px-2.5 font-medium md:px-1.5 ${page.children ? "!pr-8" : ""}`}
                  >
                    {asLinks ? (
                      <Link
                        href={page.url}
                        target={external ? "_blank" : undefined}
                        rel={external ? "noopener noreferrer" : undefined}
                      >
                        {content}
                      </Link>
                    ) : (
                      content
                    )}
                  </SidebarMenuButton>
                  {page.children && (
                    <NavSubPages
                      title={page.title}
                      url={page.url}
                      pages={page.children}
                    />
                  )}
                  {page.statusBadge && (
                    <SidebarMenuBadge
                      aria-label={`Tax application: ${page.statusBadge.label}`}
                      className={`rounded-full px-2 py-0 text-[10px] font-semibold uppercase leading-none ${page.statusBadge.className}`}
                    >
                      {page.statusBadge.label}
                    </SidebarMenuBadge>
                  )}
                  {page.badge ? (
                    <SidebarMenuBadge className="min-w-7 rounded-full bg-red-500/15 px-2 pb-0.5 font-semibold leading-none !text-red-500">
                      {page.badge}
                    </SidebarMenuBadge>
                  ) : null}
                </SidebarMenuItem>
              );
            })}
          </SidebarMenu>
        </SidebarGroupContent>
      ))}
    </>
  );
}

function NavSubPages({
  title,
  url,
  pages,
}: {
  title: string;
  url: string;
  pages: NavBranch[];
}) {
  const pathname = usePathname();
  const matches = pathname === url || pathname.startsWith(`${url}/`);
  const [open, setOpen] = useState(matches);
  const [visited, setVisited] = useState(matches);
  const id = useId();
  const { isMobile, setOpenMobile } = useSidebar();
  useEffect(() => {
    if (pathname === url || pathname.startsWith(`${url}/`)) {
      setOpen(true);
      setVisited(true);
    }
  }, [pathname, url]);
  return (
    <>
      <SidebarMenuAction
        aria-label={`${open ? "Collapse" : "Expand"} ${title}`}
        aria-expanded={open}
        aria-controls={id}
        onClick={() => {
          setVisited(true);
          setOpen((value) => !value);
        }}
      >
        <ChevronRight
          className={`transition-transform duration-200 ease-in-out motion-reduce:transition-none ${open ? "rotate-90" : ""}`}
        />
      </SidebarMenuAction>
      <div
        id={id}
        inert={!open}
        aria-hidden={!open}
        className={`grid transition-[grid-template-rows,opacity] duration-200 ease-in-out motion-reduce:transition-none ${open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}
      >
        <div className="min-h-0 overflow-hidden">
          <SidebarMenuSub className="mx-0 ml-3 translate-x-0 border-0 pb-0 pl-3 pr-0">
            {visited &&
              pages.map((page) => {
                const active =
                  pathname === page.url || pathname.startsWith(`${page.url}/`);
                return (
                  <SidebarMenuSubItem
                    key={page.url}
                    className="relative flex min-w-0 flex-col"
                  >
                    <SidebarMenuSubButton
                      asChild
                      isActive={active}
                      className={`w-full translate-x-0 transition-colors duration-200 ease-in-out ${page.children?.length ? "pr-8" : ""}`}
                    >
                      <Link
                        href={page.url}
                        aria-current={
                          pathname === page.url ? "page" : undefined
                        }
                        onClick={(event) => {
                          if (
                            event.metaKey ||
                            event.ctrlKey ||
                            event.shiftKey ||
                            event.altKey
                          )
                            return;
                          if (isMobile) setOpenMobile(false);
                          startNavigationFeedback(page.url);
                        }}
                      >
                        <span className="min-w-0 truncate">{page.title}</span>
                      </Link>
                    </SidebarMenuSubButton>
                    {!!page.children?.length && (
                      <NavSubPages
                        title={page.title}
                        url={page.url}
                        pages={page.children}
                      />
                    )}
                  </SidebarMenuSubItem>
                );
              })}
          </SidebarMenuSub>
        </div>
      </div>
    </>
  );
}
